import { lazy } from "react";
import { Theme } from "../data/style/Theme";
import DatePanel from "../components/Display/DatePanel";
import { MainText, MainTitle, scrollBar } from "../data/style/Style";

const Stack = lazy(() => import("@mui/material/Stack"));

const POLICY = [
  {
    title: "📝 Information We Collect",
    description:
      "We collect the following types of information when you use the App:",
    bullets: [
      "Name and username",
      "Email address",
      "Password (stored in encrypted form)",
      "Age",
      "Country",
      "Weight",
      "Height",
      "Weight & height (collected solely to personalize fitness recommendations and improve app functionality)",
      "Device type, operating system, and app version",
      "IP address",
      "Approximate location (country or region only, for analytics and security purposes)",
    ],
  },
  {
    title: "🔍 How We Use Your Information",
    description: "We use your information to:",
    bullets: [
      "Create and manage your user account",
      "Authenticate users and enable login functionality",
      "Provide personalized fitness content and app features",
      "Improve app performance, stability, and user experience",
      "Communicate with you regarding updates, support, or service-related messages",
      "Monitor usage trends and detect technical issues",
      "Comply with legal and regulatory obligations",
    ],
  },
  {
    title: "⚠️ Legal Bases for Processing (GDPR Users - EEA Users)",
    description:
      "If you are located in the European Economic Area (EEA), we process your personal information based on:",
    bullets: [
      "Your consent (for example, during registration)",
      "Contractual necessity (to provide the App and its services)",
      "Legitimate interests (such as analytics, app security, and service improvement)",
      "Legal obligations, where applicable",
    ],
  },
  {
    title: "🤝 Sharing Your Information",
    description:
      "We do not sell or rent your personal data. We may share data with:",
    bullets: [
      "Service providers (such as cloud hosting and mobile analytics providers) who process data on our behalf under strict data protection agreements",
      "Authorities or regulators, if required by law",
    ],
  },
  {
    title: "📊 Analytics and Tracking",
    description:
      "We use mobile analytics tools (such as Google Analytics for Firebase or equivalent) to understand how users interact with the App and to improve performance. These tools may collect:",
    bullets: [
      "Device identifiers",
      "App usage events",
      "Approximate location (non-precise)",
      "You can limit analytics tracking through your device settings where supported.",
    ],
  },
  {
    title: "🔒 Data Storage and Security",
    description:
      "We implement appropriate technical and organizational measures to protect your personal data, including encryption and secure servers. Data is stored on servers located in Germany (Nuremberg) and may be transferred or processed in other locations depending on service providers and user access location, always in compliance with applicable data protection laws.",
  },
  {
    title: "🗄️ Data Retention",
    description: "We retain your personal data only for as long as:",
    bullets: [
      "Your account remains active, or",
      "It is necessary to provide the App and its services, or",
      "Required by law",
    ],
  },
  {
    title: "🗑️ Account and Data Deletion",
    description:
      "You may request deletion of your account and associated personal data at any time by:",
    bullets: [
      "Using the account deletion option within the App (if available), or",
      "Contacting us at:" + process.env.REACT_APP_CONTACT_EMAIL,
      "Account and data deletion requests are processed within a reasonable timeframe, unless legal obligations require data retention.",
    ],
  },
  {
    title: "🛡️ Your Privacy Rights",
    description: "Depending on your location, you may have the right to:",
    bullets: [
      "Access your personal data",
      "Correct or update inaccurate data",
      "Request deletion of your data",
      "Withdraw consent at any time",
      "Object to or restrict processing",
      "Request a copy of your data (data portability)",
      "To exercise your rights, contact us at: " +
        process.env.REACT_APP_CONTACT_EMAIL,
    ],
  },
  {
    title: "👶 Children’s Privacy",
    description:
      "The App is not intended for children under the age of 16. We do not knowingly collect personal data from children under 16. If we become aware that such data has been collected without parental consent, we will delete it promptly.",
  },
  {
    title: "🔄 Changes to This Privacy Policy",
    description:
      "We may update this Privacy Policy from time to time. Any changes will be reflected by updating the “Last Updated” date and, where appropriate, by notifying users within the App.",
  },
  {
    title: "📧 Contact Us",
    description: "If you have any questions about these Terms, contact us at:",
    bullets: [process.env.REACT_APP_CONTACT_EMAIL],
  },
];

const Policy = () => {
  return (
    <Stack height={"100%"} gap={1} p={1}>
      <Stack height={"100%"} overflow={"auto"} pr={1} gap={2} sx={scrollBar}>
        <Stack ml={1} gap={2}>
          <MainTitle>Privacy Policy</MainTitle>
          <Stack alignSelf={"end"} direction={"row"} gap={3} pr={1}>
            <DatePanel label={"Effective Date"} date={"19/06/2025"} />
            <DatePanel label={"Last updated"} date={"19/06/2025"} />
          </Stack>
        </Stack>
        <Stack
          sx={{
            background: Theme.palette.card,
            border: 1,
            borderColor: Theme.palette.border,
            borderRadius: 4,
          }}
          p={1}
          gap={1}
        >
          <MainTitle>⚖️ agreement</MainTitle>
          <MainText>
            Grind Partner (“we”, “our”, or “us”) respects your privacy. This
            Privacy Policy explains how we collect, use, store, and protect
            personal information when you use the Grind Partner services,
            including:
            <br />{" "}
            <span
              style={{
                fontWeight: "bold",
                color: Theme.palette.orange,
              }}
            >
              •{" "}
            </span>{" "}
            the Grind Partner Android mobile application published on Google
            Play
            <br />{" "}
            <span
              style={{
                fontWeight: "bold",
                color: Theme.palette.orange,
              }}
            >
              •{" "}
            </span>{" "}
            the Grind Partner iOS mobile application available on the Apple App
            Store
            <br />{" "}
            <span
              style={{
                fontWeight: "bold",
                color: Theme.palette.orange,
              }}
            >
              •{" "}
            </span>{" "}
            the Grind Partner website (the “Services”). This Privacy Policy
            applies to all users of the Services.
            <br />
            By using the App, you agree to the practices described in this
            Privacy Policy.
          </MainText>
        </Stack>
        <Stack pb={3} gap={2}>
          {POLICY.map((item, index) => (
            <Stack
              key={index}
              gap={0.5}
              p={1}
              sx={{
                border: 1,
                borderRadius: 4,
                borderColor: Theme.palette.border,
                background: Theme.palette.card,
              }}
            >
              <MainTitle>{item.title}</MainTitle>
              <MainText>{item.description}</MainText>
              {item?.bullets?.map((bullet, index) => (
                <Stack key={index}>
                  <MainText>
                    <span
                      style={{
                        fontWeight: "bold",
                        color: Theme.palette.orange,
                      }}
                    >
                      •{" "}
                    </span>
                    {bullet}
                  </MainText>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Policy;
